import { ReactNode } from "react";
import {
  NumberValidation,
  OptionValidation,
  RangeValidation,
  StringValidation,
  ValidationFunction,
} from "@app/components/forms/types/validation";

export enum FormFieldType {
  Hidden = "hidden",
  Text = "text",
  Textarea = "textarea",
  Email = "email",
  Password = "password",
  URL = "url",
  Number = "number",
  Range = "range",
  Slide = "slide",
  Select = "select",
  MultiSelect = "multi-select",
  Dropdown = "dropdown",
  RadioGroup = "radio-group",
  Checkbox = "checkbox",
  CheckboxGroup = "checkbox-group",
  Switch = "switch",
  Color = "color",
  Image = "image",
  File = "file",
  Group = "group",
  Separator = "separator",
}

type Renderer =
  | string
  | ReactNode
  | ((formValues: any, formState?: any) => ReactNode);

export interface FormFieldOption {
  value: any;
  icon?: string;
  label?: Renderer;
  description?: Renderer;
}

interface FormFieldAppearance {
  appearance?: {
    className?: string;
    border?: boolean;
  };
}

export enum TriggerType {
  Show = "show",
  Enable = "enable",
}

export enum TriggerCondition {
  Equal = "eq",
  NotEqual = "neq",
  GreaterThan = "gt",
  LessThan = "lt",
  GreaterThanOrEqual = "gte",
  LessThanOrEqual = "lte",
}

interface BehaviorTrigger {
  type: TriggerType;
  field: string;
  value: any;
  condition: TriggerCondition;
}

export interface FormFieldBehavior {
  behavior?: {
    required?: boolean;
    readOnly?: boolean;
    disabled?: boolean;
    hidden?: boolean;
    triggers?: BehaviorTrigger[];
  };
}

interface FormFieldSchemaBase extends FormFieldAppearance, FormFieldBehavior {
  id: string;
  label: Renderer;
  placeholder?: string;
  layout?: "default" | "horizontal" | "vertical";
  description?: Renderer;
  defaultValue?: string | boolean | number | any;
  // 결과 반환 시 필드 값에서 제외
  exception?: boolean;
}

export interface TextInputSchema extends FormFieldSchemaBase, StringValidation {
  type: FormFieldType.Text | FormFieldType.Textarea;
}

export interface UrlInputSchema extends FormFieldSchemaBase {
  type: FormFieldType.URL;
}

export interface EmailInputSchema extends FormFieldSchemaBase {
  type: FormFieldType.Email;
}

export interface PasswordInputSchema
  extends FormFieldSchemaBase,
    StringValidation {
  type: FormFieldType.Password;
}

export interface NumberInputSchema
  extends FormFieldSchemaBase,
    NumberValidation {
  type: FormFieldType.Number;
}

export interface RangeInputSchema extends FormFieldSchemaBase, RangeValidation {
  type: FormFieldType.Range;
}

export interface BooleanInputSchema extends FormFieldSchemaBase {
  type: FormFieldType.Switch;
}

export interface ItemGroupSchema
  extends Omit<
    FormFieldSchemaBase,
    "label" | "required" | "readonly" | "defaultValue"
  > {
  type: FormFieldType.Group;
  label?: Renderer;
  noneStyle?: boolean;
  schemas: FormFieldSchema[];
}

export interface SeparatorSchema
  extends Omit<
    FormFieldSchemaBase,
    "id" | "label" | "description" | "placeholder" | "defaultValue"
  > {
  id?: string;
  type: FormFieldType.Separator;
}

export interface SelectSchema extends FormFieldSchemaBase, OptionValidation {
  type:
    | FormFieldType.Select
    | FormFieldType.MultiSelect
    | FormFieldType.Dropdown;
  options: FormFieldOption[];
}

export interface RadioGroupSchema extends FormFieldSchemaBase {
  type: FormFieldType.RadioGroup;
  options: FormFieldOption[];
  hideIndicator?: boolean; // true - 체크 표시를 보이지 않게 함. 대신 label 만 노출되며, 테두리가 생김.
  optionLayout?: "horizontal" | "vertical";
}

export interface CheckboxGroupSchema extends Omit<RadioGroupSchema, "type"> {
  type: FormFieldType.CheckboxGroup;
}

// behavior 에 대한 type 지정
export interface HiddenSchema
  extends Omit<
    FormFieldSchemaBase,
    "label" | "description" | "layout" | "placeholder"
  > {
  type: FormFieldType.Hidden;
  behavior: never;
  appearance: never;
}

export interface ColorSelectorSchema extends FormFieldSchemaBase {
  type: FormFieldType.Color;
  extraContent?: Renderer;
}

export interface OtherSchema
  extends FormFieldSchemaBase,
    ValidationFunction<any> {
  type: Exclude<
    FormFieldType,
    | FormFieldType.Text
    | FormFieldType.Textarea
    | FormFieldType.Email
    | FormFieldType.Password
    | FormFieldType.Number
    | FormFieldType.Range
    | FormFieldType.URL
    | FormFieldType.Switch
    | FormFieldType.Select
    | FormFieldType.MultiSelect
    | FormFieldType.Dropdown
    | FormFieldType.RadioGroup
    | FormFieldType.CheckboxGroup
    | FormFieldType.Group
    | FormFieldType.Color
    | FormFieldType.Separator
  >;
}

export type FormFieldSchema =
  | TextInputSchema
  | UrlInputSchema
  | EmailInputSchema
  | PasswordInputSchema
  | NumberInputSchema
  | BooleanInputSchema
  | SelectSchema
  | RadioGroupSchema
  | CheckboxGroupSchema
  | OtherSchema
  | ItemGroupSchema
  | SeparatorSchema
  | ColorSelectorSchema
  | HiddenSchema
  | RangeInputSchema;
